import { useId, useLayoutEffect, useRef, useState } from 'react'

/**
 * 칸별 입력 오류 — 키 → 안내 문구. 값이 없거나 `undefined` 면 그 칸은 정상이다.
 * 🔴 **입력값만** 담는다. 권한 · 제어락 · stale · 서버 상태 · 전송 중 · 물리 확인 · 사전 점검 경고 확인 ·
 *    「바뀐 것 없음」은 여기 넣지 않는다 — 그것들은 종전대로 주 버튼의 비활성 + 사유다.
 */
export type ValidationErrors<Key extends string> = Partial<Record<Key, string | undefined>>
export type SubmitValidator<Values, Key extends string> = (values: Values) => ValidationErrors<Key>

const FOCUSABLE = 'input, select, textarea, button, a[href], [tabindex], [contenteditable="true"]'

function isHidden(element: HTMLElement): boolean {
  if (element.closest('[hidden], [inert], [aria-hidden="true"]') !== null) return true
  for (let node: HTMLElement | null = element; node !== null; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse'
    )
      return true
  }
  return false
}

/**
 * 오류 칸 중 **DOM 순서로 첫 칸**에 포커스한다(검증기 키 순서가 아니다).
 * id 가 달린 요소가 조작부가 아니면(선택 그룹 `Segmented`) 그 안의 조작부 — 고른 구획이 있으면 그것, 없으면 첫 구획.
 * 숨김 · 비활성(`disabled` · `aria-disabled`) 조작부는 건너뛴다.
 */
function focusFirstError(root: HTMLElement, ids: ReadonlySet<string>): void {
  for (const element of root.querySelectorAll<HTMLElement>('[id]')) {
    if (!ids.has(element.id)) continue
    const inner = element.matches(FOCUSABLE)
      ? [element]
      : [
          ...element.querySelectorAll<HTMLElement>('[aria-checked="true"]'),
          ...element.querySelectorAll<HTMLElement>(FOCUSABLE),
        ]
    for (const candidate of inner) {
      if (!candidate.matches(FOCUSABLE)) continue
      if (candidate.matches(':disabled, [aria-disabled="true"], input[type="hidden"]')) continue
      if (isHidden(candidate)) continue
      candidate.focus()
      if (candidate.ownerDocument.activeElement === candidate) return
    }
  }
}

/**
 * **제출 때 검증한다**(eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」).
 * 확인 · 추가 · 저장 단추는 입력이 비거나 틀려도 **살아 있다.** 누르면 검증해서 빠진 · 틀린 칸에
 * 빨간 테두리(`aria-invalid`) + 칸 아래 안내(`Field.error`)를 세우고 **첫 오류 칸에 포커스**, 요청은 보내지 않는다.
 * 고치면 **그 칸의 표시만** 사라진다(아직 표시되지 않은 칸에 입력 중 새 오류를 세우지 않는다).
 *
 * - 규칙(검증기)은 화면이 쥔다. 훅은 오류 맵 · 제출 시점 · 고친 칸 해제 · 첫 칸 포커스만 맡는다
 * - `validate(values, root)` — 전체 검사 · 성공 여부. `root` 는 폼 또는 팝업 본문(포커스를 찾을 범위)
 * - `change(key, next, linked?)` — 고친 칸(+ 연동 칸)만 다시 잰다. **setState 뒤의 옛 값이 아니라 고친 다음 값 전체**를 넘긴다
 * - `field(key)` — `inputProps`(id · invalid · describedBy)는 입력(`TextInput` · `Combobox` · `Segmented` · `DateTimePicker`)에,
 *   `fieldProps`(htmlFor · error · errorId)는 `Field` 에. `PasswordField` 는 `id` · `error` 를 직접 받는다(`validation="submit"`)
 * - `reset()` — 팝업을 닫거나 다른 자료를 열 때
 * - 닫힌 탭 · 접힌 절에 오류 칸이 있으면 **같은 처리기 안에서** 그 탭을 연다 — 포커스는 그 렌더가 그려진 뒤에 잡힌다
 *
 * ```tsx
 * const v = useSubmitValidation((d: Draft) => ({
 *   sampleId: d.sampleId.trim() === '' ? t('validation.sampleIdRequired') : undefined,
 * }))
 * const sample = v.field('sampleId')
 *
 * <form onSubmit={(e) => {
 *   e.preventDefault()
 *   if (gate.disabled) return                      // 권한 · 락 · stale · 전송 중은 종전 게이트
 *   if (v.validate(draft, e.currentTarget)) save(draft)
 * }}>
 *   <Field label={t('column.sampleId')} required {...sample.fieldProps}>
 *     <TextInput {...sample.inputProps} value={draft.sampleId} onChange={(sampleId) => {
 *       const next = { ...draft, sampleId }
 *       setDraft(next)
 *       v.change('sampleId', next)
 *     }} />
 *   </Field>
 *   <Button type="submit" {...gate}>{t('action.confirm')}</Button>   // 입력 사유는 gate 에 넣지 않는다
 * </form>
 *
 * // 비밀번호 — 확인 칸은 같이 다시 잰다
 * v.change('password', next, ['confirm'])
 * <PasswordField id={pw.inputProps.id} validation="submit" error={pw.fieldProps.error} … />
 * ```
 */
export function useSubmitValidation<Values, Key extends string>(
  validator: SubmitValidator<Values, Key>,
) {
  const prefix = useId()
  const [errors, setErrors] = useState<ValidationErrors<Key>>({})
  const pendingFocus = useRef<HTMLElement | null>(null)
  const inputId = (key: Key) => `${prefix}-${key}`

  useLayoutEffect(() => {
    const root = pendingFocus.current
    pendingFocus.current = null
    if (root === null) return
    const ids = new Set(
      (Object.keys(errors) as Key[])
        .filter((key) => Boolean(errors[key]))
        .map((key) => `${prefix}-${key}`),
    )
    focusFirstError(root, ids)
  }, [errors, prefix])

  return {
    errors,
    /** 표시 중인 오류가 하나라도 있나 */
    hasErrors: Object.values(errors).some(Boolean),
    validate(values: Values, root: HTMLElement | null): boolean {
      const next: ValidationErrors<Key> = {}
      for (const [key, message] of Object.entries(validator(values)) as [Key, string | undefined][])
        if (message) next[key] = message
      const valid = Object.keys(next).length === 0
      pendingFocus.current = valid ? null : root
      setErrors(next)
      return valid
    },
    change(key: Key, values: Values, linked: readonly Key[] = []) {
      const checked = validator(values)
      setErrors((current) => {
        let changed = false
        const next = { ...current }
        for (const field of [key, ...linked]) {
          if (!current[field]) continue
          const message = checked[field]
          if (message) {
            if (message !== current[field]) {
              next[field] = message
              changed = true
            }
          } else {
            delete next[field]
            changed = true
          }
        }
        return changed ? next : current
      })
    },
    reset() {
      pendingFocus.current = null
      setErrors({})
    },
    field(key: Key) {
      const id = inputId(key)
      const error = errors[key] || undefined
      const errorId = `${id}-error`
      return {
        inputProps: {
          id,
          invalid: error !== undefined,
          describedBy: error === undefined ? undefined : errorId,
        },
        fieldProps: { htmlFor: id, error, errorId },
      }
    },
  }
}
